import React, { useEffect, useState } from 'react';
import PageLayout from '../components/PageLayout';
import SEOHead from '../components/SEOHead';
import { useDarkMode } from '../contexts/DarkModeContext';
import { apiGet } from '../utils/api';
import FAQBox from '../components/FAQBox';
import ReviewGrid from '../components/reviews/ReviewGrid';

const EscrowService = () => {
  const { isDarkMode } = useDarkMode();
  const [seoSettings, setSeoSettings] = useState(null);
  const [selected, setSelected] = useState('tiktok');

  const tabs = [
    { id: 'youtube', label: 'YouTube' },
    { id: 'instagram', label: 'Instagram' },
    { id: 'facebook', label: 'Facebook' },
    { id: 'tiktok', label: 'TikTok' },
  ];

  const commonSteps = [
    'The buyer pays a 4-8% ($8 minimum) service fee.',
    'The seller transfers login details and the original email account to the escrow agent.',
    'The escrow agent checks everything, changes the login details, and tells the buyer to pay the seller.',
    'The buyer pays the seller.',
    "After the seller's confirmation, the system sends the new login details to the buyer.",
  ];

  const youtubeSteps = [
    'The buyer pays a 4-8% ($3 minimum) service fee.',
    'The seller designates the escrow agent as manager.',
    'After 7 days, the seller assigns primary ownership rights to the escrow agent (7 days is the minimum amount of time required in order to assign a new primary owner in the control panel.)',
    'The escrow agent verifies everything, removes the other managers, and notifies the buyer to pay the seller.',
    'The buyer pays the seller.',
    "After the seller's confirmation, the escrow agent assigns ownership rights to the buyer.",
  ];

  const facebookSteps = [
    'The buyer pays a 4-8% ($8 minimum) service fee.',
    "The seller assigns the escrow agent's page as the administrator.",
    'The escrow agent checks everything, removes the other administrators, and tells the buyer to pay the seller.',
    'The buyer pays the seller.',
    "After the seller's confirmation, the escrow agent assigns the buyer's page as the administrator.",
  ];

  const stepsByTab = {
    youtube: youtubeSteps,
    instagram: commonSteps,
    tiktok: commonSteps,
    facebook: facebookSteps,
  };

  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);

  useEffect(() => {
    const safeReportError = async (message, context = {}) => {
      try {
        if (process.env.NODE_ENV !== 'production') return;
        const payload = {
          message: String(message || 'unknown_error'),
          context: { location: typeof window !== 'undefined' ? (window.location?.pathname || '/') : '/', ...context },
          ts: Date.now(),
        };
        await fetch('/api/client-logs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          keepalive: true,
        });
      } catch (_) {}
    };

    const loadData = async () => {
      try {
        // Load SEO settings
        const seoResponse = await apiGet('/api/page-seo/page/escrowservice');
        if (seoResponse.ok) {
          const seoData = await seoResponse.json();
          setSeoSettings(seoData);
        }

        // Load reviews
        setReviewsLoading(true);
        const reviewsResponse = await apiGet('/api/reviews/escrow?limit=9');
        if (reviewsResponse.ok) {
          const reviewsData = await reviewsResponse.json();
          setReviews(reviewsData.reviews || []);
        }
      } catch (error) {
        safeReportError('escrowservice_data_fetch_failed');
      } finally {
        setReviewsLoading(false);
      }
    };

    loadData();
  }, []);

  return (
    <PageLayout>
      <SEOHead
        title={seoSettings?.title || 'Escrow Service - YTProud'}
        description={seoSettings?.description || 'Secure your marketplace transactions with YTproud Escrow.'}
        keywords={seoSettings?.keywords || 'escrow, secure payment, buyer protection, seller protection'}
        ogTitle={seoSettings?.ogTitle || 'Escrow Service - YTProud'}
        ogDescription={seoSettings?.ogDescription || 'Secure your transactions with YTproud Escrow'}
        ogImage={seoSettings?.ogImage || '/images/about-og.jpg'}
        twitterTitle={seoSettings?.twitterTitle || 'Escrow Service - YTProud'}
        twitterDescription={seoSettings?.twitterDescription || 'Secure your transactions with YTproud Escrow'}
        twitterImage={seoSettings?.twitterImage || '/images/about-og.jpg'}
        canonical={seoSettings?.canonical || 'https://ytproud.com/escrowservice'}
        noindex={seoSettings?.noindex || false}
        nofollow={seoSettings?.nofollow || false}
        schemaEnabled={seoSettings?.schemaEnabled || false}
        schemaJsonLd={seoSettings?.customSchemaJsonLd}
      />

      <div className="w-full flex justify-center mt-4 sm:mt-10 md:mt-12 mb-4 sm:mb-10 md:mb-12 px-2 sm:px-4">
        <div className={`rounded-2xl shadow-lg px-3 sm:px-6 md:px-8 py-6 sm:py-10 md:py-12 w-full flex flex-col items-center max-w-[1525px] mx-auto transition-all duration-500 ${
          isDarkMode 
            ? 'bg-[#2a2a2a] shadow-2xl shadow-black/50 border border-gray-700/50' 
            : 'bg-white'
        }`}>
          <h1 className={`text-3xl sm:text-4xl md:text-5xl font-extrabold mb-2 sm:mb-4 text-center tracking-tight transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>Escrow Service</h1>
          <p className={`text-base sm:text-lg md:text-2xl mb-3 sm:mb-6 text-center max-w-2xl transition-colors duration-300 ${
            isDarkMode ? 'text-gray-300' : 'text-gray-600'
          }`}>Protect buyers and sellers with secure, milestone‑based payments.</p>
          <div className="w-24 sm:w-32 h-1 bg-gradient-to-r from-red-500 to-blue-500 rounded-full mb-1" />
        </div>
      </div>

      {/* Procedure section with platform tabs */}
      <div className="max-w-[1525px] mx-auto px-2 sm:px-4 py-6 sm:py-12 md:py-16">
        <h2 className={`text-3xl sm:text-4xl md:text-5xl font-extrabold text-center mb-6 sm:mb-10 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          How escrow works
        </h2>

        {/* Tabs */}
        <div className="w-full">
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 mb-6 sm:mb-8">
            {tabs.map(t => (
              <button
                key={t.id}
                onClick={() => setSelected(t.id)}
                className={`px-5 sm:px-6 py-2.5 rounded-full border text-sm sm:text-base font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  selected === t.id
                    ? (isDarkMode
                      ? 'bg-white text-black border-white/10 focus:ring-white/20'
                      : 'bg-black text-white border-black focus:ring-black/20')
                    : (isDarkMode
                      ? 'bg-transparent text-gray-200 border-gray-700 hover:bg-[#1f1f1f]'
                      : 'bg-white text-gray-800 border-gray-200 hover:bg-gray-50')
                }`}
                aria-pressed={selected === t.id}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Steps card */}
          <div className={`rounded-3xl p-6 sm:p-10 shadow-2xl transition-colors duration-300 ${
            isDarkMode ? 'bg-[#0e0e0e] border border-blue-900/30 ring-1 ring-blue-800/20' : 'bg-white border border-blue-100 ring-1 ring-blue-100'
          }`}>
            <ol className={`space-y-5 list-decimal pl-6 sm:pl-8 text-base sm:text-lg ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
              {stepsByTab[selected].map((step, idx) => (
                <li key={idx} className="leading-relaxed">
                  {step}
                </li>
              ))}
            </ol>

            {/* Payment Icons – match footer style (boxed) */}
            <div className="flex items-center justify-center gap-3 sm:gap-4 mt-8 opacity-95" role="group" aria-label="Accepted payment methods">
              <span className="inline-flex" title="Visa" aria-hidden="true">
                <svg className="w-12 h-8" viewBox="0 0 48 32">
                  <rect width="48" height="32" rx="6" fill="#fff" />
                  <text x="24" y="21" textAnchor="middle" fontSize="14" fill="#1a237e" fontWeight="bold">VISA</text>
                </svg>
              </span>
              <span className="inline-flex" title="MasterCard" aria-hidden="true">
                <svg className="w-12 h-8" viewBox="0 0 48 32">
                  <rect width="48" height="32" rx="6" fill="#fff" />
                  <circle cx="18" cy="16" r="8" fill="#ff9800" />
                  <circle cx="30" cy="16" r="8" fill="#e53935" fillOpacity=".8" />
                </svg>
              </span>
              <span className="inline-flex" title="PayPal" aria-hidden="true">
                <svg className="w-12 h-8" viewBox="0 0 48 32">
                  <rect width="48" height="32" rx="6" fill="#fff" />
                  <text x="24" y="21" textAnchor="middle" fontSize="14" fill="#1565c0" fontWeight="bold">PayPal</text>
                </svg>
              </span>
              <span className="inline-flex" title="Wise" aria-hidden="true">
                <svg className="w-12 h-8" viewBox="0 0 48 32">
                  <rect width="48" height="32" rx="6" fill="#fff" />
                  <text x="24" y="21" textAnchor="middle" fontSize="14" fill="#00bfae" fontWeight="bold">WISE</text>
                </svg>
              </span>
              <span className="inline-flex" title="Bitcoin" aria-hidden="true">
                <svg className="w-12 h-8" viewBox="0 0 48 32">
                  <rect width="48" height="32" rx="6" fill="#fff" />
                  <circle cx="24" cy="16" r="8" fill="#ffb300" />
                  <text x="24" y="21" textAnchor="middle" fontSize="14" fill="#fff" fontWeight="bold">₿</text>
                </svg>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Removed step cards and contact button per request */}

      <div className={`relative overflow-hidden py-8 sm:py-16 md:py-20 rounded-3xl transition-all duration-500 ${
        isDarkMode
          ? 'bg-gradient-to-br from-[#1c1c1c] via-[#111] to-[#1c1c1c] shadow-2xl shadow-black/50 border border-gray-700/60'
          : 'bg-gradient-to-br from-white to-gray-50'
      }`}>
        {/* subtle decorative blobs */}
        {isDarkMode && (
          <React.Fragment>
            <div className="absolute -top-10 -left-10 w-48 h-48 bg-blue-500/10 blur-3xl rounded-full" />
            <div className="absolute -bottom-10 -right-10 w-56 h-56 bg-purple-500/10 blur-3xl rounded-full" />
          </React.Fragment>
        )}
        <div className="relative max-w-[1525px] mx-auto px-2 sm:px-6">
          <h2 className={`text-2xl sm:text-3xl md:text-4xl font-extrabold mb-6 sm:mb-10 text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Why Use Escrow?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-8">
            <div className={`group p-6 sm:p-8 rounded-2xl border shadow-xl transition-all duration-300 hover:-translate-y-1 ${
              isDarkMode ? 'bg-[#0f0f0f] border-gray-700 hover:shadow-black/40' : 'bg-white border-gray-200 hover:shadow-2xl'
            }`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white font-bold shadow-md">1</div>
                <h3 className={`text-lg sm:text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Buyer Protection</h3>
              </div>
              <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Funds aren’t released until the account transfer is confirmed.</p>
            </div>

            <div className={`group p-6 sm:p-8 rounded-2xl border shadow-xl transition-all duration-300 hover:-translate-y-1 ${
              isDarkMode ? 'bg-[#0f0f0f] border-gray-700 hover:shadow-black/40' : 'bg-white border-gray-200 hover:shadow-2xl'
            }`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold shadow-md">2</div>
                <h3 className={`text-lg sm:text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Seller Confidence</h3>
              </div>
              <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Payment is guaranteed and released upon buyer confirmation.</p>
            </div>

            <div className={`group p-6 sm:p-8 rounded-2xl border shadow-xl transition-all duration-300 hover:-translate-y-1 ${
              isDarkMode ? 'bg-[#0f0f0f] border-gray-700 hover:shadow-black/40' : 'bg-white border-gray-200 hover:shadow-2xl'
            }`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center text-white font-bold shadow-md">3</div>
                <h3 className={`text-lg sm:text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Admin Verification</h3>
              </div>
              <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Our team verifies the transfer details to reduce risk for both parties.</p>
            </div>
          </div>

          {/* mini stats row */}
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
            <div className={`${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
              <div className="text-2xl font-extrabold">50K+</div>
              <div className="text-xs opacity-70">Protected transactions</div>
            </div>
            <div className={`${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
              <div className="text-2xl font-extrabold">24/7</div>
              <div className="text-xs opacity-70">Agent support</div>
            </div>
            <div className={`${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
              <div className="text-2xl font-extrabold">A+</div>
              <div className="text-xs opacity-70">User satisfaction</div>
            </div>
            <div className={`${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
              <div className="text-2xl font-extrabold">Zero</div>
              <div className="text-xs opacity-70">Chargebacks via escrow</div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Reviews */}
      <div className="max-w-[1525px] mx-auto px-2 sm:px-4 py-8 sm:py-12">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h3 className={`text-xl sm:text-2xl font-extrabold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Recent Reviews</h3>
          <a href="/marketplace" className="hidden sm:inline text-sm font-semibold text-blue-600 dark:text-blue-400 hover:underline">View marketplace</a>
        </div>
        
        {reviewsLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className={`rounded-2xl p-5 shadow-lg animate-pulse ${isDarkMode ? 'bg-[#1a1a1a] border border-gray-700' : 'bg-white border border-gray-200'}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
                  <div className="flex-1">
                    <div className={`h-4 w-24 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} mb-2`}></div>
                    <div className={`h-3 w-16 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
                  </div>
                </div>
                <div className={`h-16 w-full rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} mb-2`}></div>
                <div className={`h-3 w-20 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
              </div>
            ))}
          </div>
        ) : reviews.length > 0 ? (
          <ReviewGrid reviews={reviews} initialCount={9} step={9} />
        ) : (
          <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            <p className="text-lg">No reviews yet. Be the first to share your experience!</p>
          </div>
        )}
      </div>

      {/* Escrow FAQs - same UI as marketplace */}
      <FAQBox
        title="Escrow FAQs"
        faqs={[
          { 
            q: 'What is YTProud\'s escrow service and how does it work?', 
            a: 'YTProud\'s escrow holds buyer funds securely until the social media account is transferred. The seller only receives payment after the buyer confirms ownership. This protects both parties and ensures every deal is safe, transparent, and fraud-free.' 
          },
          { 
            q: 'Why should I use escrow when buying or selling accounts?', 
            a: 'Escrow adds an extra layer of protection. Buyers know their money is safe until the account is delivered, and sellers are guaranteed payment once transfer is complete. It removes risk from peer-to-peer deals and builds trust on both sides.' 
          },
          { 
            q: 'How long does the escrow process take on YTProud?', 
            a: 'Most transfers are completed within 24–72 hours, depending on the platform and account type. Escrow ensures payment is only released after a verified, successful handover, making the process fast and secure.' 
          },
          { 
            q: 'What happens if there\'s a dispute during the transfer?', 
            a: 'If an issue arises, YTProud\'s escrow team steps in to review the case. Funds remain locked until the dispute is resolved. Our buyer protection policy ensures fair handling, so you never lose money or accounts to fraud.' 
          },
          { 
            q: 'Are all transactions on YTProud protected by escrow?', 
            a: 'Yes. Every transaction on YTProud goes through escrow by default. This guarantees secure payments, verified transfers, and a safe environment for both buyers and sellers.' 
          },
        ]}
      />
    </PageLayout>
  );
};

export default EscrowService;


